import React from 'react';
import { API_BASE_URL } from '../config/api';

export const CR80_W = 646;
export const CR80_H = 408;

const HEADER_H = 72;
const FOOTER_H = 36;
const BODY_H   = CR80_H - HEADER_H - FOOTER_H;
const PHOTO_W  = Math.round(CR80_W * 0.35); // 35% — enlarged

// ── Guilloche background (shared by front & back) ─────────────────────────────
export const GuillocheSVG = () => (
  <svg width={CR80_W} height={CR80_H} viewBox={`0 0 ${CR80_W} ${CR80_H}`}
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="cg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor="#daeef7" />
        <stop offset="50%"  stopColor="#eaf4f9" />
        <stop offset="100%" stopColor="#f0e8e0" />
      </linearGradient>
      <pattern id="gw" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
        <path d="M0 11 Q5.5 0 11 11 Q16.5 22 22 11" fill="none" stroke="#8bbdd4" strokeWidth="0.3" opacity="0.38"/>
        <path d="M0 11 Q5.5 22 11 11 Q16.5 0 22 11" fill="none" stroke="#c4a882" strokeWidth="0.22" opacity="0.26"/>
      </pattern>
    </defs>
    <rect width={CR80_W} height={CR80_H} fill="url(#cg)" />
    <rect width={CR80_W} height={CR80_H} fill="url(#gw)" />
    {Array.from({ length: 28 }).map((_, i) => (
      <path key={i}
        d={`M0,${i*15} Q${CR80_W*.25},${i*15-10} ${CR80_W*.5},${i*15} Q${CR80_W*.75},${i*15+10} ${CR80_W},${i*15}`}
        fill="none" stroke={i%2===0?'#7ab4cc':'#b8956a'} strokeWidth="0.26" opacity="0.16"
      />
    ))}
    <text x={CR80_W*.68} y={CR80_H*.54} textAnchor="middle" dominantBaseline="middle"
      fontSize="130" opacity="0.05" fill="#0b2545"
      style={{ fontFamily:'serif', userSelect:'none' }}>⚜</text>
  </svg>
);

// ── School logo in circular seal ──────────────────────────────────────────────
const SchoolLogo = ({ size = 50 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', background: '#fff',
    border: '2px solid rgba(232,213,160,0.95)',
    boxShadow: '0 0 0 1.5px rgba(11,37,69,0.55), 0 2px 8px rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', flexShrink: 0,
  }}>
    <img src="/logo.png" alt="TRS"
      style={{ width: '84%', height: '84%', objectFit: 'contain' }}
      onError={e => { e.target.style.display='none'; e.target.parentElement.innerText='⚜'; }}
    />
  </div>
);

// ── FRONT CARD ────────────────────────────────────────────────────────────────
const TeacherIdCard = ({ teacher, academicYear = '2026-2027', overrides = {} }) => {
  const d = { ...teacher, ...overrides };

  const empId      = d.empId || d.employeeId || d.studentId
    || `TRS-EMP-2026-${String(d._id||'001').slice(-3).padStart(3,'0')}`;
  const rawDesig   = d.role==='tuition_teacher' ? 'Tuition Teacher'
    : d.designation || (d.class ? `Class ${d.class} Teacher` : 'Senior Teacher');
  const subject    = d.subject ? d.subject.toUpperCase() : '';
  const desigLine  = subject ? `${rawDesig.toUpperCase()} | ${subject}` : rawDesig.toUpperCase();
  const issueDate  = d.issueDate  || '15 JUN 2026';
  const validThru  = d.validThru  || `31 MAY ${parseInt(academicYear.split('-')[1])||2029}`;
  const bloodGroup = d.bloodGroup || 'N/A';
  const contact    = d.contact || d.mobile || d.phone || '+91 8209683549';

  // Photo: local blob URL takes priority, then server URL
  const photoUrl = overrides.photoBlob
    ? overrides.photoBlob
    : d.profilePicture
      ? (d.profilePicture.startsWith('http') ? d.profilePicture : `${API_BASE_URL}${d.profilePicture}`)
      : null;

  return (
    <div style={{
      width: CR80_W, height: CR80_H, borderRadius: 22,
      position: 'relative', overflow: 'hidden', boxSizing: 'border-box',
      fontFamily: '"Arial","Helvetica Neue",sans-serif',
      boxShadow: '0 6px 28px rgba(11,37,69,0.25)',
      border: '1.5px solid rgba(11,37,69,0.2)',
    }}>
      <GuillocheSVG />

      {/* HEADER */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_H,
        background: '#0B2545', borderBottom: '2.5px solid #c9a84c',
        display: 'flex', alignItems: 'center', padding: '0 14px', gap: 12, zIndex: 2,
      }}>
        <SchoolLogo size={50} />
        <div style={{ flex: 1, lineHeight: 1 }}>
          <div style={{ color:'#fff', fontSize:17, fontWeight:900, letterSpacing:'1.2px',
            textTransform:'uppercase', fontFamily:'Georgia,"Times New Roman",serif' }}>
            THE RISING SCHOOL
          </div>
          <div style={{ color:'#F0C040', fontSize:14, fontWeight:700, letterSpacing:'1px', marginTop:4, textTransform:'uppercase' }}>
            MANAGED BY THE SYNERGY FOUNDATION
          </div>
          <div style={{ color:'#fff', fontSize:14, fontWeight:700, letterSpacing:'0.8px', marginTop:3, textTransform:'uppercase', opacity:0.88 }}>
            FACULTY IDENTITY CARD
          </div>
        </div>
      </div>

      {/* BODY */}
      <div style={{
        position: 'absolute', top: HEADER_H, left: 0, right: 0, height: BODY_H,
        display: 'flex', zIndex: 2,
      }}>
        {/* Photo block */}
        <div style={{
          width: PHOTO_W, height: BODY_H, flexShrink: 0,
          borderRight: '1.5px solid #0B2545',
          background: '#e4ecf3',
          position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {photoUrl ? (
            <img src={photoUrl} alt={d.name}
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center top',
                display: 'block',
                imageRendering: '-webkit-optimize-contrast',
              }}
            />
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              width: '100%', height: '100%', gap: 6,
            }}>
              <div style={{ fontSize: 38, opacity: 0.3 }}>👤</div>
              <div style={{ fontSize: 14, color: '#9ca3af', letterSpacing: '1px', fontWeight: 600 }}>PHOTO</div>
            </div>
          )}
          {/* White matte inner frame */}
          <div style={{
            position: 'absolute', inset: 5,
            border: '1.5px solid #0B2545', borderRadius: 2, pointerEvents: 'none',
          }} />
        </div>

        {/* Data */}
        <div style={{
          flex: 1, padding: '10px 14px 8px 14px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden',
        }}>
          <div>
            <div style={{
              fontSize: 24, fontWeight: 900, color: '#000',
              textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.15,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{(d.name||'N/A').toUpperCase()}</div>
            <div style={{
              fontSize: 18, fontWeight: 700, color: '#1A365D', marginTop: 4,
              letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{desigLine}</div>
            <div style={{
              height: 1.5, background: 'linear-gradient(90deg,#0B2545,#c9a84c,transparent)',
              marginTop: 7, marginBottom: 7, borderRadius: 1,
            }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 14px', flex: 1 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 12.5, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>EMPLOYEE ID</div>
              <div style={{ fontSize: 16.5, fontWeight: 800, color: '#000', fontFamily: '"Courier New",monospace', letterSpacing: '0.8px', marginTop: 1 }}>{empId}</div>
            </div>
            <div>
              <div style={{ fontSize: 12.5, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>DATE OF ISSUE</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#000', marginTop: 1 }}>{issueDate}</div>
            </div>
            <div>
              <div style={{ fontSize: 12.5, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>VALID THRU</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#b91c1c', marginTop: 1 }}>{validThru}</div>
            </div>
            <div>
              <div style={{ fontSize: 12.5, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>BLOOD GROUP</div>
              <div style={{
                fontSize: 16.5, fontWeight: 900, color: '#b91c1c',
                background: 'rgba(185,28,28,0.07)', border: '1px solid rgba(185,28,28,0.25)',
                borderRadius: 3, padding: '1px 5px', display: 'inline-block', marginTop: 1,
              }}>{bloodGroup}</div>
            </div>
            <div>
              <div style={{ fontSize: 12.5, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>CONTACT</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#000', fontFamily: '"Courier New",monospace', marginTop: 1 }}>{contact}</div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: FOOTER_H,
        background: '#0B2545', borderTop: '2px solid #c9a84c',
        display: 'flex', alignItems: 'center', padding: '0 14px', zIndex: 3,
      }}>
        <div style={{ flex: 1, fontSize: 12.5, color: 'rgba(255,255,255,0.82)', letterSpacing: '0.3px', lineHeight: 1.5 }}>
          <div>Sector 12 Savina, Udaipur, Rajasthan — 313002</div>
          <div>Ph: +91 8209683549 &nbsp;|&nbsp; www.therisingschool.com</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ borderTop: '1px solid rgba(201,168,76,0.7)', paddingTop: 3,
            fontSize: 12, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
            Authorized Signatory
          </div>
          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>The Rising School</div>
        </div>
      </div>
    </div>
  );
};

export default TeacherIdCard;
